import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel,
  MenuItem, Paper, Select, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Typography
} from '@mui/material';
import { Download, History as HistoryIcon, Preview, Refresh, BarChart as BarChartIcon } from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadShelfLifeExpiryReportCsv,
  downloadShelfLifeExpiryReportHistoryCsv,
  downloadShelfLifeExpiryReportHistoryPdf,
  downloadShelfLifeExpiryReportPdf,
  fetchShelfLifeExpiryReportHistory,
  fetchShelfLifeExpiryReportPreviewData,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const EXPIRY_MODES = [
  { value: 'ALL_WITH_EXPIRY', label: 'All items with expiry date' },
  { value: 'EXPIRED', label: 'Expired only' },
  { value: 'EXPIRING_SOON', label: 'Expiring within days' }
];
const STORE_TYPES = [
  { value: '', label: 'All store types' },
  { value: 'BONDED', label: 'Bonded' },
  { value: 'POL', label: 'POL' },
  { value: 'QUARANTINE', label: 'Quarantine' },
  { value: 'SCRAPE', label: 'Scrape' },
  { value: 'TOOLS', label: 'Tools' }
];
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'IN_INVENTORY', label: 'In Inventory' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'QUARANTINED', label: 'Quarantined' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'UNAVAILABLE', label: 'Unavailable' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const ShelfLifeExpiryReportPanel = () => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [expiryMode, setExpiryMode] = useState('EXPIRING_SOON');
  const [daysAhead, setDaysAhead] = useState(30);
  const [storeType, setStoreType] = useState('');
  const [status, setStatus] = useState('IN_INVENTORY');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchShelfLifeExpiryReportHistory());
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => { if (subTab === 1) loadHistory(); }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    expiryMode, daysAhead, storeType, status, search, includeArchived,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [expiryMode, daysAhead, storeType, status, search, includeArchived, dateFrom, dateTo, reportDate]);

  const busy = previewing || downloading || downloadingCsv || visualizing;

  const runPreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      setPreviewReportData(await fetchShelfLifeExpiryReportPreviewData(reportPayload));
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally { setPreviewing(false); }
  };

  const runVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      setVisualizeData(await fetchShelfLifeExpiryReportPreviewData(reportPayload));
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally { setVisualizing(false); }
  };

  const runDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadShelfLifeExpiryReportPdf(
        { ...reportPayload, visualizations },
        `Shelf_Life_Expiry_Report_${fileStamp}.pdf`
      );
      showToast('Shelf-Life Expiry Report downloaded and saved to history', 'success');
      if (subTab === 1) loadHistory();
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally { setDownloading(false); }
  };

  const runCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadShelfLifeExpiryReportCsv(reportPayload, `Shelf_Life_Expiry_Report_${fileStamp}.pdf`);
      showToast('Shelf-Life Expiry Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally { setDownloadingCsv(false); }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Due Report — Expired Items / Shelf-Life Expiry</Typography>
      <Typography color="text.secondary" paragraph>
        Inventory items tracked with shelf-life expiry dates. Filter expired stock or items due within a number of days.
      </Typography>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <FormControl fullWidth>
              <InputLabel>Expiry mode</InputLabel>
              <Select value={expiryMode} label="Expiry mode" onChange={(e) => setExpiryMode(e.target.value)}>
                {EXPIRY_MODES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {expiryMode === 'EXPIRING_SOON' && (
              <TextField
                label="Days ahead"
                type="number"
                value={daysAhead}
                onChange={(e) => setDaysAhead(Number(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0 }}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Store type</InputLabel>
              <Select value={storeType} label="Store type" onChange={(e) => setStoreType(e.target.value)}>
                {STORE_TYPES.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth placeholder="Part number, store code…" />
            <FormControlLabel control={<Switch checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />} label="Include archived items" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — filters by expiry date; leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — filters by expiry date; leave blank for all records"
              />
            </Stack>
            {dateRangeError && <Alert severity="warning">{dateRangeError}</Alert>}
            <TextField label="Report Date" type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />} onClick={runVisualize} disabled={busy || Boolean(dateRangeError)}>Visualize</Button>
              <Button variant="outlined" startIcon={previewing ? <CircularProgress size={16} /> : <Preview />} onClick={runPreview} disabled={busy || Boolean(dateRangeError)}>Preview</Button>
              <Button variant="contained" startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />} onClick={() => runDownload()} disabled={busy || Boolean(dateRangeError)}>Download PDF</Button>
              <Button variant="outlined" startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />} onClick={runCsv} disabled={busy || Boolean(dateRangeError)}>Download CSV</Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded Shelf-Life Reports</Typography>
            <Button size="small" startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />} onClick={loadHistory} disabled={historyLoading}>Refresh</Button>
          </Box>
          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center"><CircularProgress size={28} /></Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No downloads yet. Generate one from the Generate tab.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{row.expiryMode}{row.expiryMode === 'EXPIRING_SOON' ? ` (${row.daysAhead}d)` : ''}</TableCell>
                    <TableCell>{row.storeType || 'All'}</TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" startIcon={<Download />} onClick={() => downloadShelfLifeExpiryReportHistoryPdf(row._id, row.filename)}>PDF</Button>
                        <Button size="small" startIcon={<Download />} onClick={() => downloadShelfLifeExpiryReportHistoryCsv(row._id, row.filename)}>CSV</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog open={previewOpen} title="Shelf-Life Expiry preview" reportData={previewReportData} onClose={() => { setPreviewOpen(false); setPreviewReportData(null); }} onDownload={() => runDownload()} downloading={downloading} onDownloadCsv={runCsv} downloadingCsv={downloadingCsv} />
      <ReportVisualizeDialog open={visualizeOpen} title="Shelf-Life Expiry Report" data={visualizeData} downloading={downloading} onClose={() => { setVisualizeOpen(false); setVisualizeData(null); }} onDownloadPdfWithVisualization={(v) => runDownload(v)} />
    </Box>
  );
};

export default ShelfLifeExpiryReportPanel;
